<template>
  <v-form>
    <v-container fluid>

      <v-row>
        <h3>{{ $t('order.request') }}</h3>
      </v-row>
      <v-row>
        <v-col cols="12" sm="6" md="6">
          <!--          Reference-->
          <v-text-field
              v-model="item.ref"
              :error-messages="refErrors"
              :label="$t('order.ref')"
              required
              @input="$v.item.ref.$touch()"
              @blur="$v.item.ref.$touch()"
          />
        </v-col>
        <v-col cols="12" sm="6" md="6">
          <!--          DesignFile-->
          <v-combobox
              v-if="designfileSelectItems"
              v-model="item.designFile"
              :items="designfileSelectItems"
              :error-messages="designfileErrors"
              :no-data-text="$t('global.no_result')"
              :placeholder="$t('global.no_select')"
              clearable
              :label="$t('designfile.designfile')"
              chips
              item-value="@id"
              item-text="name"
          >
            <template v-slot:selection>
              <v-chip
                  :key="item.designFile.name"
              >
                <v-icon @mouseover="hover = true"
                        @mouseleave="hover = false"
                        left color="warning" v-if="item.designFile.nda">mdi-lock</v-icon>
                <v-icon left v-else>mdi-lock-open</v-icon>
                {{item.designFile.name}}
              </v-chip>
            </template>
          </v-combobox>
          <span v-if="hover" class="warning--text">{{$t('designfile.confidential')}}</span>
          <v-expansion-panels v-model="expansionOpen"  @click="openExpansionPanel">
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('designfile.add') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>

                <DesignFileCreate :sub="true" @close="closeExpansionPanel" :customer="item.customer" :type="item.type"></DesignFileCreate>

              </v-expansion-panel-content>

            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="6" md="6">
          <!--          Customer-->
          <v-combobox
              v-if="customerSelectItems"
              v-model="item.customer"
              :items="customerSelectItems"
              :error-messages="customerErrors"
              :no-data-text="$t('global.no_result')"
              :placeholder="$t('global.no_select')"
              clearable
              :label="$t('customer.customer')"
              item-text="name"
              item-value="@id"
          />
        </v-col>
        <v-col cols="12" sm="6" md="6">
          <!--          Type -->
          <v-combobox
              v-if="typeSelectItems"
              v-model="item.type"
              :items="typeSelectItems"
              :error-messages="typeErrors"
              :no-data-text="$t('global.no_result')"
              :placeholder="$t('global.no_select')"
              clearable
              :label="$t('order.type')"
              item-text="name"
              item-value="@id"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="6" md="6">
          <!--          Commercial-->
          <v-combobox
              v-model="item.commercial"
              :items="enabledCommercialSelectItems"
              :error-messages="CommercialErrors"
              :no-data-text="$t('global.no_result')"
              :placeholder="$t('global.no_select')"
              clearable
              :label="$t('order.claimant')"
              item-text="login"
              item-value="@id"
          />
        </v-col>

        <v-col cols="12" sm="6" md="6">
          <!--          Quantity-->
          <v-text-field
              v-model.number="item.quantity"
              :error-messages="quantityErrors"
              :label="$t('order.quantity')"
              required
              @input="$v.item.quantity.$touch()"
              @blur="$v.item.quantity.$touch()"
          />
        </v-col>

      </v-row>
      <v-row>
        <v-col cols="12" sm="6" md="6">
          <!--          Price-->
          <v-text-field
              v-model.number="item.price"
              :error-messages="priceErrors"
              :label="$t('order.unitPrice')"
              @input="$v.item.price.$touch()"
              @blur="$v.item.price.$touch()"
          />

        </v-col>
        <v-col cols="12" sm="6" md="6">
          <!--          Tags-->
          <v-combobox
              v-if="tagSelectItems"
              v-model="item.tags"
              :items="tagSelectItems"
              :error-messages="tagErrors"
              :no-data-text="$t('global.no_result')"
              :placeholder="$t('global.no_select')"
              clearable
              :label="$t('order.tags')"
              item-text="name"
              item-value="@id"
              :search-input.sync="search"
              multiple
              chips
              :return-object="true"
              variant="solo-inverted"
              @change="changeTag()"
          >
            <template v-slot:no-data>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ $t('global.no_result') }} "<strong>{{ search }}</strong>". {{ $t('global.addcombo1') }} <kbd>enter</kbd> {{ $t('global.addcombo2') }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
            <template v-slot:selection="{ attrs, item, parent, selected }">
              <v-chip
                  v-if="item === Object(item)"
                  v-bind="attrs"
                  color="accent"
                  :input-value="selected"
                  label
              >
                     <span class="pr-2">
                       {{ item.name }}
                     </span>
                <v-icon
                    small
                    @click="parent.selectItem(item)"
                >
                  mdi-close
                </v-icon>
              </v-chip>
            </template>
          </v-combobox>
        </v-col>
      </v-row>
      <v-row>
          <v-col cols="12" sm="6" md="6">
            <!--          Sector-->
            <v-combobox
                v-if="sectorSelectItems"
                v-model="item.sector"
                :items="sectorSelectItems"
                :error-messages="sectorErrors"
                :no-data-text="$t('global.no_result')"
                :placeholder="$t('global.no_select')"
                clearable
                :label="$t('order.sector')"
                item-text="name"
                item-value="@id"
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <!--          Verticale-->
            <v-combobox
                v-if="verticaleSelectItems"
                v-model="item.verticale"
                :items="verticaleSelectItems"
                :error-messages="verticaleErrors"
                :no-data-text="$t('global.no_result')"
                :placeholder="$t('global.no_select')"
                clearable
                :label="$t('order.verticale')"
                item-text="name"
                item-value="@id"
            />
          </v-col>
        </v-row>


      <!--Labo -->

      <v-row>
        <h3>{{ $t('order.fabrication') }}</h3>
      </v-row>

      <v-row>
        <v-col cols="12" sm="6" md="6">
          <!--            End of production date client-->
          <InputDate
              v-model="item.deliveryObjectif"
              :label="$t('order.delivery')"
              :errormessages="deliveryObjectifErrors"
          />
          <!--            End of production interne-->
          <InputDate
              v-model="item.delivery"
              :label="$t('order.delivery_interne')"
              :errormessages="deliveryErrors"
          />
        </v-col>

        <v-col cols="12" sm="6" md="6">
          <!--            Production file-->
          <v-combobox
              v-if="prodfileSelectItems"
              v-model="item.prodfile"
              :items="prodfileSelectItems"
              :error-messages="prodfileErrors"
              :no-data-text="$t('global.no_result')"
              :placeholder="$t('global.no_select')"
              clearable
              :label="$t('prodfile.prodfile')"
              item-text="name"
              item-value="@id"
          />
          <v-expansion-panels v-model="boolExpansionPanel" width="500">
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{$t('prodfile.add')}}
              </v-expansion-panel-header>
              <v-expansion-panel-content>

                <ProdFileCreate :sub="true" @close="close" :designfile="item.designFile"/>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>


      </v-row>
      <v-row>
        <!--          Priority-->
        <v-col cols="12" sm="6" md="6">
          <v-combobox
              v-if="prioritySelectItems"
              v-model="item.priority"
              :items="prioritySelectItems"
              :error-messages="priorityErrors"
              :no-data-text="$t('global.no_result')"
              :placeholder="$t('global.no_select')"
              clearable
              :label="$t('priority.priority')"
              item-text="name"
              item-value="@id"
          />
        </v-col>

        <v-col cols="12" sm="6" md="6">
          <!--            Risk level-->
          <v-combobox
              v-if="riskSelectItems"
              v-model="item.risk"
              :items="riskSelectItems"
              :error-messages="riskErrors"
              :no-data-text="$t('global.no_result')"
              :placeholder="$t('global.no_select')"
              clearable
              :label="$t('order.risk')"
              item-text="comment"
              item-value="@id"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="6" md="6">
          <!--          Material-->
          <v-combobox
              v-model="item.material"
              :items="materialSelectItems"
              :error-messages="materialErrors"
              :no-data-text="$t('global.no_result')"
              :placeholder="$t('global.no_select')"
              clearable
              :label="$t('order.material')"
              item-text="name"
              item-value="@id"
          />
        </v-col>
        <v-col cols="12" sm="6" md="6">
            <v-combobox
                v-if="orderresolutionSelectItems"
                v-model="item.resolution"
                :items="orderresolutionSelectItems"
                :error-messages="resolutionErrors"
                :no-data-text="$t('global.no_result')"
                :placeholder="$t('global.no_select')"
                clearable
                :label="$t('order.resolution')"
                item-text="name"
                item-value="@id"
            />
          </v-col>
         </v-row>
         <v-row>
        <v-col cols="12" sm="6" md="6">
          <!--          Cost-->
          <v-text-field
              v-model.number="item.cost"
              :error-messages="costErrors"
              :label="$t('order.unitCost')"
              @input="$v.item.cost.$touch()"
              @blur="$v.item.cost.$touch()"
          />

        </v-col>
        <v-col cols="12" sm="6" md="6">
          <!--          Failed items-->
          <v-text-field
              v-model.number="item.failedItems"
              :error-messages="failedItemsErrors"
              :label="$t('order.failedItems')"
              @input="$v.item.failedItems.$touch()"
              @blur="$v.item.failedItems.$touch()"
          />

        </v-col>

      </v-row>
        <!--          Operator-->
      <v-row>
        <v-col cols="12" sm="6" md="6">
          <v-combobox
              v-model="item.operator"
              :items="enabledOperatorSelectItems"
              :error-messages="OperatorErrors"
              :no-data-text="$t('global.no_result')"
              :placeholder="$t('global.no_select')"
              clearable
              :label="$t('order.operator')"
              item-text="login"
              item-value="@id"
          />
        </v-col>
        <v-col cols="12" sm="6" md="6">
          <!--          Quotation-->
          <v-text-field
              v-model="item.quotation"
              :error-messages="costErrors"
              :label="$t('order.quotation')"
              @input="$v.item.quotation.$touch()"
              @blur="$v.item.quotation.$touch()"
          />

        </v-col>
      </v-row>

      <v-row>
        <h3>{{ $t('order.comment') }}</h3>
      </v-row>

      <v-row>
        <v-col cols="12" sm="12" md="12">
          <!--          Comment -->
          <v-textarea
              v-model="item.comment"
              :error-messages="commentErrors"
              :label="$t('order.comment')"
              auto-grow
              rows="1"
              row-height="15"
              @input="$v.item.comment.$touch()"
              @blur="$v.item.comment.$touch()"
          />
        </v-col>
      </v-row>

    </v-container>
    <Loading :visible="isLoading" />
  </v-form>
</template>

<script>
import has from 'lodash/has';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { mapActions } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import InputDate from "../InputDate.vue";
import Loading from '../../components/Loading';
import { date } from '../../validators/date';
import DesignFileCreate from "@/views/designfile/Create.vue";
import ProdFileCreate from "@/views/prodfile/Create.vue";

export default {
  name: 'OrderForm',
  mixins: [validationMixin],
  components: {
    ProdFileCreate,
    DesignFileCreate,
    InputDate,
    Loading,
  },
  props: {
    values: {
      type: Object,
      required: true
    },
    defaultvalues: {
      type: Object,
      required: false
    },
    errors: {
      type: Object,
      default: () => {}
    },

    initialValues: {
      type: Object,
      default: () => {}
    }
  },

  mounted() {
    this.designfileGetSelectItems();
    this.prodfileGetSelectItems();
    this.riskGetSelectItems();
    this.customerGetSelectItems();
    this.typeGetSelectItems();
    this.priorityGetSelectItems();
    this.operatorGetSelectItems();
    this.sectorGetSelectItems();
    this.commercialGetSelectItems();
    this.tagGetSelectItems();
    this.materialGetSelectItems();
    this.verticaleGetSelectItems();
    this.orderresolutionGetSelectItems();
  },

  data() {
    return {
      ref: null,
      designFile: null,
      prodfile: null,
      customer: null,
      sector: null,
      type: null,
      priority: null,
      quantity: null,
      delivery: null,
      deliveryObjectif: null,
      comment: null,
      tags: null,
      material: null,
      search: null,
      price: null,
      cost: null,
      failedItems: null,
      verticale: null,
      risk: null,
      quotation: null,
      resolution: null,
      boolExpansionPanel: true,
      expansionOpen: null,
      hover: false,
    };
  },

  computed: {
    isLoading() {
      return (this.isLoading_type ||
          this.isLoading_customer ||
          this.isLoading_designfile ||
          this.isLoading_prodfile ||
          this.isLoading_priority ||
          this.isLoading_tag ||
          this.isLoading_sector ||
          this.isLoading_commercial ||
          this.isLoading_operator ||
          this.isLoading_material ||
          this.isLoading_verticale ||
          this.isLoading_orderresolution
      );
    },

    ...mapFields('customer', {
      customerSelectItems: 'selectItems',
      isLoading_customer : 'isLoading'
    }),
    ...mapFields('risk', {
      riskSelectItems: 'selectItems',
      isLoading_risk : 'isLoading'
    }),
    ...mapFields('designfile', {
      designfileSelectItems: 'selectItems',
      isLoading_designfile : 'isLoading'
    }),
    ...mapFields('prodfile', {
      prodfileSelectItems: 'selectItems',
      isLoading_prodfile : 'isLoading'
    }),
    ...mapFields('material', {
      materialSelectItems: 'selectItems',
      isLoading_material : 'isLoading'
    }),
    ...mapFields('ordertype', {
      typeSelectItems: 'selectItems',
      isLoading_type : 'isLoading'
    }),
    ...mapFields('sector', {
      sectorSelectItems: 'selectItems',
      isLoading_sector : 'isLoading'
    }),

    ...mapFields('priority', {
      prioritySelectItems: 'selectItems',
      isLoading_priority : 'isLoading'
    }),
    ...mapFields('user', {
      commercialSelectItems: 'selectItems',
      isLoading_commercial : 'isLoading'
    }),
    ...mapFields('user', {
      operatorSelectItems: 'selectItems',
      isLoading_operator : 'isLoading'
    }),
    ...mapFields('tag', {
      tagSelectItems: 'selectItems',
      isLoading_tag : 'isLoading'
    }),
    ...mapFields('verticale', {
      verticaleSelectItems: 'selectItems',
      isLoading_verticale : 'isLoading'
    }),
    ...mapFields('orderresolution', {
      orderresolutionSelectItems: 'selectItems',
      isLoading_orderresolution: 'isLoading'
    }),


    // eslint-disable-next-line
    item() {
      return this.initialValues || this.values;
    },
    refErrors() {
      const errors = [];

      if (!this.$v.item.ref.$dirty) return errors;

      has(this.violations, 'ref') && errors.push(this.violations.ref);

      !this.$v.item.ref.required && errors.push(this.$t('global.field_required'));

      return errors;
    },
    designfileErrors() {
      const errors = [];
      if (!this.$v.item.designFile.$dirty) return errors;
      
      has(this.violations, 'designFile') && errors.push(this.violations.designFile);

      return errors;
    },
    prodfileErrors() {
      const errors = [];
      if (!this.$v.item.prodfile.$dirty) return errors;
      has(this.violations, 'prodfile') && errors.push(this.violations.prodfile);

      return errors;
    },
    customerErrors() {
      const errors = [];
      if (!this.$v.item.customer.$dirty) return errors;
      has(this.violations, 'customer') && errors.push(this.violations.customer);

      return errors;
    },
    riskErrors() {
      const errors = [];

      if (!this.$v.item.risk.$dirty) return errors;

      has(this.violations, 'risk') && errors.push(this.violations.risk);

      return errors;
    },
    typeErrors() {
      const errors = [];

      if (!this.$v.item.type.$dirty) return errors;

      has(this.violations, 'type') && errors.push(this.violations.type);


      return errors;
    },
    materialErrors() {
      const errors = [];

      if (!this.$v.item.material.$dirty) return errors;

      has(this.violations, 'material') && errors.push(this.violations.material);


      return errors;
    },
    priorityErrors() {
      const errors = [];
      if (!this.$v.item.priority.$dirty) return errors;
      has(this.violations, 'priority') && errors.push(this.violations.priority);

      return errors;
    },
    deliveryErrors() {
      const errors = [];
      
      if (!this.$v.item.delivery.$dirty) return errors;

      has(this.violations, 'delivery') && errors.push(this.violations.delivery);

      return errors;
    },
    deliveryObjectifErrors() {
      const errors = [];

      if (!this.$v.item.deliveryObjectif.$dirty) return errors;

      has(this.violations, 'deliveryObjectif') && errors.push(this.violations.deliveryObjectif);

      return errors;
    },
    quantityErrors() {
      const errors = [];

      if (!this.$v.item.quantity.$dirty) return errors;

      has(this.violations, 'quantity') && errors.push(this.violations.quantity);

      !this.$v.item.quantity.required && errors.push(this.$t('global.field_required'));

      return errors;
    },
    OperatorErrors() {
      const errors = [];

      if (!this.$v.item.operator.$dirty) return errors;

      has(this.violations, 'operator') && errors.push(this.violations.operator);

      return errors;
    },
    CommercialErrors() {
      const errors = [];

      if (!this.$v.item.commercial.$dirty) return errors;

      has(this.violations, 'commercial') && errors.push(this.violations.commercial);

      !this.$v.item.commercial.required && errors.push(this.$t('global.field_required'));

      return errors;
    },
    priceErrors() {
      const errors = [];

      if (!this.$v.item.price.$dirty) return errors;

      has(this.violations, 'price') && errors.push(this.violations.price);

      return errors;
    },
    costErrors() {
      const errors = [];

      if (!this.$v.item.cost.$dirty) return errors;

      has(this.violations, 'cost') && errors.push(this.violations.cost);

      return errors;
    },
    failedItemsErrors() {
      const errors = [];

      if (!this.$v.item.failedItems.$dirty) return errors;

      has(this.violations, 'failedItems') && errors.push(this.violations.failedItems);

      return errors;
    },
    quotationErrors() {
      const errors = [];

      if (!this.$v.item.quotation.$dirty) return errors;

      has(this.violations, 'quotation') && errors.push(this.violations.quotation);

      return errors;
    },
    tagErrors() {
      const errors = [];

      if (!this.$v.item.tags.$dirty) return errors;

      has(this.violations, 'tags') && errors.push(this.violations.tags);


      return errors;
    },
    sectorErrors() {
      const errors = [];

      if (!this.$v.item.sector.$dirty) return errors;

      has(this.violations, 'sector') && errors.push(this.violations.sector);

      return errors;
    },
    commentErrors() {
      const errors = [];

      if (!this.$v.item.comment.$dirty) return errors;

      has(this.violations, 'comment') && errors.push(this.violations.comment);


      return errors;
    },
    verticaleErrors() {
      const errors = [];

      if (!this.$v.item.verticale.$dirty) return errors;

      has(this.violations, 'verticale') && errors.push(this.violations.verticale);

      return errors;
    },
    resolutionErrors() {
      const errors = [];

      if (!this.$v.item.resolution.$dirty) return errors;

      has(this.violations, 'resolution') && errors.push(this.violations.resolution);


      return errors;
    },
    violations() {
      return this.errors || {};
    },
    enabledCommercialSelectItems() {
      if (this.commercialSelectItems) {
        return this.commercialSelectItems.filter(item => item.enabled && !item.supervision);
      }
      return [];
    },
    enabledOperatorSelectItems() {
      if (this.operatorSelectItems) {
        return this.operatorSelectItems.filter(item => item.enabled && !item.supervision);
      }
      return [];
    },
  },

  methods: {
    closeExpansionPanelProd() {
      this.boolExpansionPanel = ! this.boolExpansionPanel
    },
    close() {
      this.closeExpansionPanelProd()
      this.prodfileGetSelectItems()
    },

    closeExpansionPanel() {
      this.expansionOpen = null
      this.designfileGetSelectItems()
    },
    openExpansionPanel() {
      this.expansionOpen = 0
    },
    ...mapActions({
      designfileGetSelectItems: 'designfile/fetchSelectItems',
      prodfileGetSelectItems: 'prodfile/fetchSelectItems',
      customerGetSelectItems: 'customer/fetchSelectItems',
      typeGetSelectItems: 'ordertype/fetchSelectItems',
      priorityGetSelectItems: 'priority/fetchSelectItems',
      commercialGetSelectItems: 'user/fetchSelectItems',
      sectorGetSelectItems:'sector/fetchSelectItems',
      operatorGetSelectItems: 'user/fetchSelectItems',
      tagGetSelectItems: 'tag/fetchSelectItems',
      materialGetSelectItems: 'material/fetchSelectItems',
      riskGetSelectItems: 'risk/fetchSelectItems',
      verticaleGetSelectItems: 'verticale/fetchSelectItems',
      orderresolutionGetSelectItems: 'orderresolution/fetchSelectItems',
    }),
    changeTag() {
      this.item.tags = this.item.tags.map(v => {
        if (typeof v === 'string') {
          v = {
            name: v,
          }
        }
        return v
      })
      const seen = new Set(); //évite les doublons
      this.item.tags = this.item.tags.filter(tag => { //parcours des tags
        const key = tag.name.toLowerCase(); //évite la casse
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      this.search = "";
    },
  },

  watch: {
    prioritySelectItems() {
      if (this.item.priority == null ) {
        for (var i = 0; i < this.prioritySelectItems.length; i++) {
          if (this.prioritySelectItems[i].init ) {
            this.item.priority = this.prioritySelectItems[i];
          }
        }
      }
    },

    riskSelectItems() {
      if (this.item.risk == null) {
        for (let i = 0; i < this.riskSelectItems.length; i++) {
          if (this.riskSelectItems[i].init) {
            this.item.risk = this.riskSelectItems[i];
          }
        }
      }
    },

  designfileSelectItems() {
    if (this.$route.params) {
      if (this.item.designFile == null && (this.$route.params.parent != null)) {
        for (var i = 0; i < this.designfileSelectItems.length; i++) {
          if (this.designfileSelectItems[i]['@id'] == this.$route.params.parent ) {
            this.item.designFile = this.designfileSelectItems[i];
            /*
              if (this.item.type == null) {
                console.log();
                this.item.type = this.designfileSelectItems[i].type;
              }
            */
          }
        }
      }
    }
  },
  orderresolutionSelectItems() {
      if (this.item.resolution == null ) {
        for (var i = 0; i < this.orderresolutionSelectItems.length; i++) {
          if (this.orderresolutionSelectItems[i].init ) {
            this.item.resolution = this.orderresolutionSelectItems[i];
          }
        }
      }
    },

},

validations: {
  item: {
    ref: {
      required,
    },
    designFile: {
    },
    prodfile: {
    },
    customer: {
    },
    tags: {
    },
    type: {
    },
    price: {
    },
    cost: {
    },
    quotation: {
    },
    priority: {
    },
    quantity: {
      required,
    },
    sector:{
      },
    operator: {
    },
    commercial: {
      required,
    },
    delivery: {
      date,
    },
    deliveryObjectif: {
      date,
    },
    comment: {
    },
    material: {
    },
    risk: {
    },
    verticale:{
    },
    resolution:{ 
    },
    failedItems: {
    }
  }
}
};
</script>
